import { Heading1, Type, MousePointerClick, Minus, ArrowUp, ArrowDown, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { TOKEN, personalize } from "@/lib/crm";

const uid = () => Math.random().toString(36).slice(2, 9);
export const block = (type, p = {}) => ({ id: uid(), type, text: "", label: "", url: "", ...p });

export const TEMPLATE_BLOCKS = {
  blank: () => [block("text")],
  welcome: () => [
    block("headline", { text: "Welcome to AVY, {{First Name}}!" }),
    block("text", { text: "Thank you for your interest in AVY. We help busy professionals reclaim their time with trained Virtual Assistants." }),
    block("button", { label: "See how it works", url: "https://avy.com" }),
    block("divider"),
    block("text", { text: "Reply to this email any time with questions.\n\nWarmly,\nThe AVY Team" }),
  ],
  discovery: () => [
    block("headline", { text: "{{First Name}}, let's book your discovery call" }),
    block("text", { text: "We'd love to learn about your business on a free 20-minute discovery call. Pick a time that works for you and we'll handle the rest." }),
    block("button", { label: "Book my call", url: "https://avy.com/book" }),
    block("divider"),
    block("text", { text: "Best,\nThe AVY Team" }),
  ],
  checkin: () => [
    block("headline", { text: "Quick check-in, {{First Name}}" }),
    block("text", { text: "Just checking in to see how things are going. Is there anything AVY can help you with this month?" }),
    block("divider"),
    block("text", { text: "Talk soon,\nThe AVY Team" }),
  ],
};

export const blocksToText = (blocks) =>
  blocks.map((b) => (b.type === "divider" ? "——" : b.type === "button" ? `[${b.label}](${b.url})` : b.text)).filter(Boolean).join("\n\n");

export const blocksHaveContent = (blocks) => blocks.some((b) => (b.type === "button" ? b.label.trim() : b.type === "divider" ? false : b.text.trim()));

const ADD = [["headline", Heading1, "Headline"], ["text", Type, "Text"], ["button", MousePointerClick, "Button"], ["divider", Minus, "Divider"]];

export function BlockEditor({ blocks, onChange }) {
  const upd = (id, p) => onChange(blocks.map((b) => (b.id === id ? { ...b, ...p } : b)));
  const move = (i, d) => { const n = [...blocks]; const [b] = n.splice(i, 1); n.splice(i + d, 0, b); onChange(n); };
  return (
    <div className="space-y-3" data-testid="block-editor">
      {blocks.map((b, i) => (
        <div key={b.id} className="rounded-xl border bg-white p-3 space-y-2" data-testid={`block-${b.type}-${i}`}>
          <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-stone-500 font-semibold">
            <span className="flex-1">{b.type}</span>
            {(b.type === "headline" || b.type === "text") && <button type="button" className="text-[#C59B27] normal-case tracking-normal text-xs" onClick={() => upd(b.id, { text: `${b.text}${b.text && !b.text.endsWith(" ") ? " " : ""}${TOKEN}` })} data-testid={`block-token-${i}`}>+ {TOKEN}</button>}
            <button type="button" disabled={i === 0} onClick={() => move(i, -1)} className="p-1 disabled:opacity-30 hover:text-[#3B185F]" data-testid={`block-up-${i}`}><ArrowUp className="h-3.5 w-3.5" /></button>
            <button type="button" disabled={i === blocks.length - 1} onClick={() => move(i, 1)} className="p-1 disabled:opacity-30 hover:text-[#3B185F]" data-testid={`block-down-${i}`}><ArrowDown className="h-3.5 w-3.5" /></button>
            <button type="button" onClick={() => onChange(blocks.filter((x) => x.id !== b.id))} className="p-1 hover:text-red-600" data-testid={`block-remove-${i}`}><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
          {b.type === "headline" && <Input value={b.text} placeholder="Headline" className="font-display font-bold text-lg h-11" onChange={(e) => upd(b.id, { text: e.target.value })} data-testid={`block-headline-input-${i}`} />}
          {b.type === "text" && <Textarea rows={4} value={b.text} placeholder="Write your paragraph…" onChange={(e) => upd(b.id, { text: e.target.value })} data-testid={`block-text-input-${i}`} />}
          {b.type === "button" && (
            <div className="grid grid-cols-2 gap-2">
              <Input value={b.label} placeholder="Button label" onChange={(e) => upd(b.id, { label: e.target.value })} data-testid={`block-button-label-${i}`} />
              <Input value={b.url} placeholder="https://…" onChange={(e) => upd(b.id, { url: e.target.value })} data-testid={`block-button-url-${i}`} />
            </div>
          )}
          {b.type === "divider" && <div className="h-px bg-stone-200 my-1" />}
        </div>
      ))}
      <div className="flex flex-wrap gap-2">
        {ADD.map(([t, Icon, l]) => (
          <Button key={t} type="button" size="sm" variant="outline" className="border-[#3B185F]/25" onClick={() => onChange([...blocks, block(t)])} data-testid={`add-block-${t}`}><Icon className="h-3.5 w-3.5 mr-1.5" />{l}</Button>
        ))}
      </div>
    </div>
  );
}

export function EmailPreview({ subject, blocks, body, contact, fromName, fromEmail }) {
  return (
    <div className="rounded-xl border bg-stone-100 p-3 sm:p-5" data-testid="email-preview">
      <div className="mx-auto max-w-md rounded-lg bg-white shadow-sm overflow-hidden">
        <div className="border-b px-5 py-3 text-xs text-stone-500">
          <div><span className="font-semibold text-stone-700">From:</span> {fromName} &lt;{fromEmail}&gt;</div>
          <div><span className="font-semibold text-stone-700">To:</span> {contact?.email || "recipient"}</div>
          <div className="mt-1 text-sm font-semibold text-[#19082B]" data-testid="email-preview-subject">{personalize(subject, contact) || "(no subject)"}</div>
        </div>
        <div className="h-1 bg-[#3B185F]" />
        <div className="px-6 py-6 space-y-4 text-sm text-stone-700">
          {blocks?.length ? blocks.map((b) => {
            if (b.type === "headline") return <h2 key={b.id} className="font-display text-2xl font-extrabold text-[#19082B] leading-tight">{personalize(b.text, contact)}</h2>;
            if (b.type === "text") return <p key={b.id} className="whitespace-pre-wrap leading-relaxed">{personalize(b.text, contact)}</p>;
            if (b.type === "button") return <div key={b.id}><span className="inline-block rounded-full bg-[#D4AF37] px-5 py-2.5 font-semibold text-[#19082B]">{b.label || "Button"}</span></div>;
            return <hr key={b.id} className="border-stone-200" />;
          }) : <p className="whitespace-pre-wrap leading-relaxed">{personalize(body, contact)}</p>}
        </div>
        <div className="border-t px-6 py-3 text-[11px] text-stone-400">{fromName} · You're receiving this because you're in touch with AVY.</div>
      </div>
    </div>
  );
}
